import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Star } from "lucide-react";
import { MaskedLine, Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { Seo } from "@/components/Seo";
import { IMG, SERVICES, PROJECTS, TESTIMONIALS } from "@/data/content";

const stats = [
  ["20+", "Years of Craft"],
  ["1,200+", "Floors Installed"],
  ["100%", "Satisfaction Focus"],
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);

  return (
    <div data-testid="home-page">
      <Seo
        title="COC Flooring Ltd — Crafted Floors, Enduring Impressions"
        description="COC Flooring Ltd designs and installs hardwood, herringbone, LVT, tiling, laminate and carpet with craftsman precision. Request a free estimate."
      />
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[100svh] flex items-end overflow-hidden bg-[#121110]">
        <motion.div style={{ y: imgY }} className="absolute inset-0">
          <img
            src={IMG.hero}
            alt="Crafted hardwood floor in an architectural interior"
            className="w-full h-[115%] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#121110] via-[#121110]/55 to-[#121110]/25" />
        </motion.div>

        <motion.div
          style={{ y: textY }}
          className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-16 lg:pb-24 pt-40 w-full"
        >
          <MaskedLine delay={0.15}>
            <span className="inline-flex items-center gap-3 border border-[#C87D32]/50 text-[#E9C093] font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] px-4 py-2 rounded-full">
              <span className="w-1.5 h-1.5 rotate-45 bg-[#C87D32]" />
              Architectural Flooring Atelier
            </span>
          </MaskedLine>

          <h1 className="mt-8 font-serif text-[#FAF8F5] text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.06] tracking-tight max-w-4xl">
            <MaskedLine delay={0.3}>The art of</MaskedLine>
            <MaskedLine delay={0.42}>
              <em className="text-[#E9C093] not-italic font-light italic">exceptional</em> flooring
            </MaskedLine>
            <MaskedLine delay={0.54} className="text-outline font-semibold uppercase">
              underfoot
            </MaskedLine>
          </h1>

          <MaskedLine delay={0.7}>
            <p className="mt-6 max-w-xl text-[#A39E93] font-light text-base sm:text-lg leading-relaxed">
              COC Flooring Ltd designs and installs hardwood, LVT, tile, laminate
              and carpet with the precision of joinery — for homes and spaces
              that demand more.
            </p>
          </MaskedLine>

          <MaskedLine delay={0.85}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/gallery"
                data-testid="hero-primary-cta"
                className="group inline-flex items-center gap-3 bg-primary text-primary-foreground font-mono text-xs uppercase tracking-[0.2em] px-7 py-4 rounded-full hover:bg-[#9A5B1D] transition-colors duration-300"
              >
                Explore Portfolio
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-secondary-cta"
                className="inline-flex items-center gap-3 border border-[#FAF8F5]/30 text-[#FAF8F5] font-mono text-xs uppercase tracking-[0.2em] px-7 py-4 rounded-full hover:border-[#C87D32] hover:text-[#E9C093] transition-colors duration-300"
              >
                Get Free Estimate
              </Link>
            </div>
          </MaskedLine>

          <MaskedLine delay={1}>
            <div className="mt-14 pt-8 border-t border-[#FAF8F5]/10 grid grid-cols-3 gap-6 max-w-2xl">
              {stats.map(([n, l]) => (
                <div key={l}>
                  <div className="font-serif text-2xl sm:text-4xl text-[#FAF8F5]">{n}</div>
                  <div className="mt-1 font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.25em] text-[#A39E93]">
                    {l}
                  </div>
                </div>
              ))}
            </div>
          </MaskedLine>
        </motion.div>
      </section>

      <Marquee />

      {/* SERVICES PREVIEW */}
      <section className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary font-semibold">
                  What we lay
                </p>
                <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight max-w-xl">
                  Six disciplines, one standard of finish
                </h2>
              </div>
              <Link
                to="/services"
                data-testid="services-view-all-link"
                className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-primary transition-colors"
              >
                All services
                <ArrowUpRight size={15} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {SERVICES.slice(0, 3).map((s, i) => (
              <Reveal key={s.id} delay={i * 0.12}>
                <Link
                  to="/services"
                  data-testid={`home-service-card-${s.id}`}
                  className="group block"
                >
                  <div className="overflow-hidden rounded-xl aspect-[4/3]">
                    <img
                      src={s.img}
                      alt={s.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  </div>
                  <div className="mt-5 flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-serif text-xl sm:text-2xl group-hover:text-primary transition-colors">
                        {s.title}
                      </h3>
                      <p className="mt-2 text-sm text-muted-foreground font-light leading-relaxed">
                        {s.tags.join(" · ")}
                      </p>
                    </div>
                    <span className="mt-1 shrink-0 w-9 h-9 rounded-full border border-border flex items-center justify-center text-muted-foreground group-hover:bg-primary group-hover:border-primary group-hover:text-primary-foreground transition-all duration-300">
                      <ArrowUpRight size={15} />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED WORK */}
      <section className="py-20 lg:py-32 bg-[#181715] text-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#C87D32] font-semibold">
              Selected work
            </p>
            <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight max-w-2xl">
              Floors that hold the room together
            </h2>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-6 auto-rows-[240px]">
            {PROJECTS.slice(0, 3).map((p, i) => (
              <Reveal key={p.id} delay={i * 0.1} className={`${p.span} h-full`}>
                <Link
                  to="/gallery"
                  data-testid={`home-project-${p.id}`}
                  className="group relative block h-full overflow-hidden rounded-xl"
                >
                  <img
                    src={p.url}
                    alt={p.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                  <div className="absolute bottom-0 inset-x-0 p-6 flex items-end justify-between gap-4">
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#E9C093]">
                        {p.category} — {p.location}
                      </p>
                      <h3 className="mt-2 font-serif text-lg sm:text-xl text-[#FAF8F5]">
                        {p.title}
                      </h3>
                    </div>
                    <span className="shrink-0 w-9 h-9 rounded-full bg-[#FAF8F5]/10 backdrop-blur border border-[#FAF8F5]/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <ArrowUpRight size={15} />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary font-semibold">
              Kind words
            </p>
            <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight">
              Trusted by homeowners &amp; architects
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.12}>
                <figure
                  data-testid={`testimonial-card-${i}`}
                  className="h-full bg-card border border-border rounded-xl p-8 flex flex-col"
                >
                  <div className="flex gap-1 text-primary">
                    {Array.from({ length: t.rating }).map((_, s) => (
                      <Star key={s} size={14} fill="currentColor" />
                    ))}
                  </div>
                  <blockquote className="mt-5 font-serif text-lg leading-relaxed flex-1">
                    “{t.quote}”
                  </blockquote>
                  <figcaption className="mt-6 pt-5 border-t border-border">
                    <div className="font-semibold text-sm">{t.name}</div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">
                      {t.role}
                    </div>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="pb-20 lg:pb-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <Reveal>
            <div className="relative overflow-hidden rounded-2xl bg-[#181715] px-8 py-16 lg:px-20 lg:py-24">
              <div
                className="absolute -right-24 -top-24 w-96 h-96 rounded-full opacity-20"
                style={{ background: "radial-gradient(circle, #C87D32 0%, transparent 65%)" }}
              />
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#C87D32] font-semibold">
                Ready when you are
              </p>
              <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl text-[#FAF8F5] tracking-tight max-w-2xl">
                Let’s measure your space — the estimate is free
              </h2>
              <Link
                to="/contact"
                data-testid="cta-band-quote-button"
                className="mt-10 inline-flex items-center gap-3 bg-primary text-primary-foreground font-mono text-xs uppercase tracking-[0.2em] px-8 py-4 rounded-full hover:bg-[#9A5B1D] transition-colors duration-300"
              >
                Request a Quote <ArrowRight size={15} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
